import type { EventDTO } from '@/services/activity.service'

/**
 * The recorded payload as JSON, or `null` when there is nothing to show.
 *
 * `data` is a `Record<string, unknown>` straight off the wire and nothing has
 * validated its shape, so all three failure modes — absent, empty, or holding a
 * value `JSON.stringify` refuses (it throws on a cycle or a BigInt, and returns
 * `undefined` rather than a string for some inputs) — collapse to one `null`
 * that the empty state renders from.
 */
function formatPayload(data: EventDTO['data'] | undefined): string | null {
	if (!data || Object.keys(data).length === 0) return null
	try {
		return JSON.stringify(data, null, 2) ?? null
	} catch {
		return null
	}
}

function MetaRow({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex gap-2">
			<dt className="w-20 flex-none text-ds-text-subtle">{label}</dt>
			<dd className="min-w-0 break-all text-ds-text-muted">{value}</dd>
		</div>
	)
}

/**
 * An audit event as it was recorded: what it is, then its payload verbatim.
 *
 * Raw JSON is the interim answer to "what actually changed" for every event type
 * at once, in place of a dedicated view per type. Shared by every surface that
 * expands an entry - the workspace audit-log section, the detail page's Activity
 * tab, and the Administration audit log - because the panel is the same evidence
 * in all of them; only the row chrome above it differs.
 */
export function EventPayloadDetail({ event, actorName }: { event: EventDTO; actorName: string }) {
	const payload = formatPayload(event.data)

	return (
		<div className="mb-2.5 rounded-ds-md border border-ds-border/60 bg-ds-surface p-3 font-mono text-ds-xs">
			<dl className="space-y-1">
				{/* The timestamp verbatim, not the row's friendly one: on an evidence
				    surface the recorded instant is the value being inspected. */}
				<MetaRow label="time" value={event.timestamp} />
				<MetaRow label="actor" value={actorName} />
				<MetaRow
					label="entity"
					value={event.entityType && event.entityId ? `${event.entityType} · ${event.entityId}` : '--'}
				/>
				<MetaRow label="type" value={event.type} />
			</dl>
			<p className="mt-3 mb-1 text-ds-text-subtle uppercase">payload</p>
			{payload ? (
				<pre className="max-h-72 overflow-auto rounded-ds-sm bg-ds-surface-elevated p-2.5">{payload}</pre>
			) : (
				<p className="text-ds-text-muted">No payload was recorded for this event.</p>
			)}
		</div>
	)
}
